import { useMutation } from "@tanstack/react-query";
import { objNamesToSnakeCase } from "common/utils/helpers/cases";
import { type models } from "jiffy-api";
import { uploadArtworkWithTracking } from "common/utils/dtf-artwork/uploadArtworkWithTracking";
import {
  DEFAULT_TIMEOUT,
  LARGE_FILE_TIMEOUT,
  LARGE_FILE_THRESHOLD
} from "@design-lab/shared/constants";
import { isApiError } from "@design-lab/shared/lib/errorGuards";

/**
 * Creates a React Query mutation for uploading artwork to the DTF API.
 *
 * The mutation accepts an upload request object, sends it as multipart form data
 * (converting field names to snake_case), selects a timeout based on the file size,
 * and normalizes connection timeouts into an error with `response.data.error_type === "timeout_error"`.
 *
 * @returns A React Query mutation object that resolves with the raw API response on success. On failure it rethrows API errors; connection timeouts are converted to an error with `response.data.error_type` set to `"timeout_error"` and `response.status` set to `504`.
 */
export function useDftUploadArtwork() {
  return useMutation({
    retry: (failureCount: number, error: unknown) => {
      const errorData = isApiError(error) ? error.response?.data : undefined;

      // Timeout errors should be retryable
      const isTimeoutError = errorData?.error_type === "timeout_error";
      if (isTimeoutError) {
        return failureCount < 2;
      }

      // Validation errors should not be retried
      const isValidationError =
        errorData?.error_type === "backend_file_validation" || errorData?.validation_error;

      if (isValidationError) {
        return false;
      }

      return failureCount < 2;
    },
    networkMode: "always",
    mutationFn: async (params: models.dtf.uploadArtwork.Request) => {
      const formData = new FormData();
      const fields = objNamesToSnakeCase(params);

      for (const [key, value] of Object.entries(fields)) {
        if (key === "file") {
          formData.append(key, params.file);
        } else {
          formData.append(key, String(value));
        }
      }

      const fileSize = params.file?.size || 0;
      const timeout = fileSize > LARGE_FILE_THRESHOLD ? LARGE_FILE_TIMEOUT : DEFAULT_TIMEOUT;

      // HB-1948: route through helper for HB-422 suppression + Mixpanel coverage.
      // The helper logs upload errors then re-throws so the outer catch below
      // still runs and can normalize ECONNABORTED/504 timeouts.
      const pageInfo = { pageValue: "design-lab", businessLine: "Transfers" };

      try {
        return await uploadArtworkWithTracking(formData, pageInfo, {
          raw: true,
          useAxiosForRequest: true,
          headers: { "Content-Type": undefined },
          timeout
        });
      } catch (error: unknown) {
        const isEconnAborted =
          error instanceof Error && (error as { code?: string }).code === "ECONNABORTED";
        const is504 = isApiError(error) && error.response?.status === 504;
        if (isEconnAborted || is504) {
          const timeoutError = Object.assign(new Error("Request timeout"), {
            response: {
              data: {
                error_type: "timeout_error",
                error:
                  "Upload timed out. The file may be taking too long to process or your connection may be slow. Please try again.",
                message: "Request timeout"
              },
              status: 504
            }
          });
          throw timeoutError;
        }
        throw error;
      }
    }
  });
}
